import { renderNav } from '../components/nav.js';
import { FlockWS, type BroadcastRecord, type WSState } from '../ws-client.js';
import { flockServerWsUrl } from '../config.js';
import { matchRule } from '../raven-core/index.js';
import { getLoadedKeys, getKeyForFrequency } from '../key-handoff.js';

const MAX_RENDERED_ROWS = 200;

interface FirehoseState {
  paused: boolean;
  buffered: BroadcastRecord[];
  rendered: BroadcastRecord[];
  uniqueFrequencies: Set<string>;
  totalSeen: number;
}

function timestampDisplay(ms: number): string {
  return new Date(ms).toISOString().slice(11, 19);
}

function rowHtml(b: BroadcastRecord): string {
  const text = escapeHtml(b.carrier_text);
  const key = getKeyForFrequency(b.frequency);
  const matched = key !== undefined && matchRule(b.carrier_text, key.encodingRule);
  const klass = matched ? 'broadcast-row match' : 'broadcast-row';
  const href = `#/frequency/${encodeURIComponent(b.frequency)}`;
  return `
    <div class="${klass}" data-id="${b.id}">
      <a class="freq" href="${href}">${escapeHtml(b.frequency)}</a>
      <div class="text">${text}</div>
      <div class="ts">${timestampDisplay(b.stored_at)}</div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function mountFirehose(root: HTMLElement): () => void {
  const keysLoaded = getLoadedKeys() !== null;
  root.innerHTML = `
    ${renderNav({ active: 'firehose' })}
    <div class="firehose-page">
      <header class="firehose-header">
        <div class="firehose-title">Firehose · all frequencies</div>
        <div>
          <span class="firehose-controls">
            <button data-action="resume" class="active">streaming</button>
            <button data-action="pause">paused</button>
          </span>
          <span class="firehose-buffer-badge" data-buffer-count></span>
          ${keysLoaded ? '<span class="firehose-scanning">scanning for ravens</span>' : ''}
        </div>
      </header>
      <main class="firehose-stream" data-stream>
        <div class="firehose-empty" data-empty>connecting to spectrum…</div>
      </main>
      <footer class="firehose-footer">
        <span><span data-broadcast-count>0</span> broadcasts seen</span>
        <span><span data-frequency-count>0</span> frequencies</span>
      </footer>
    </div>
  `;

  const stream = root.querySelector<HTMLElement>('[data-stream]');
  const emptyEl = root.querySelector<HTMLElement>('[data-empty]');
  const bufferCount = root.querySelector<HTMLElement>('[data-buffer-count]');
  const broadcastCount = root.querySelector<HTMLElement>('[data-broadcast-count]');
  const frequencyCount = root.querySelector<HTMLElement>('[data-frequency-count]');
  const resumeBtn = root.querySelector<HTMLButtonElement>('button[data-action="resume"]');
  const pauseBtn = root.querySelector<HTMLButtonElement>('button[data-action="pause"]');

  if (
    stream === null ||
    bufferCount === null ||
    broadcastCount === null ||
    frequencyCount === null ||
    resumeBtn === null ||
    pauseBtn === null
  ) {
    return () => undefined;
  }

  const state: FirehoseState = {
    paused: false,
    buffered: [],
    rendered: [],
    uniqueFrequencies: new Set<string>(),
    totalSeen: 0,
  };

  const renderRow = (b: BroadcastRecord): void => {
    if (emptyEl !== null && emptyEl.parentElement === stream) {
      emptyEl.remove();
    }
    state.rendered.push(b);
    stream.insertAdjacentHTML('beforeend', rowHtml(b));
    while (state.rendered.length > MAX_RENDERED_ROWS) {
      state.rendered.shift();
      const first = stream.firstElementChild;
      if (first !== null) first.remove();
    }
    stream.scrollTop = stream.scrollHeight;
  };

  const recordCounts = (b: BroadcastRecord): void => {
    state.totalSeen++;
    state.uniqueFrequencies.add(b.frequency);
    broadcastCount.textContent = String(state.totalSeen);
    frequencyCount.textContent = String(state.uniqueFrequencies.size);
  };

  const updateBufferBadge = (): void => {
    bufferCount.textContent = state.buffered.length === 0 ? '' : `${state.buffered.length} buffered`;
  };

  const onBroadcast = (b: BroadcastRecord): void => {
    recordCounts(b);
    if (state.paused) {
      state.buffered.push(b);
      updateBufferBadge();
    } else {
      renderRow(b);
    }
  };

  const setPaused = (paused: boolean): void => {
    state.paused = paused;
    resumeBtn.classList.toggle('active', !paused);
    pauseBtn.classList.toggle('active', paused);
    if (!paused && state.buffered.length > 0) {
      const drain = state.buffered;
      state.buffered = [];
      for (const b of drain) renderRow(b);
      updateBufferBadge();
    }
  };

  resumeBtn.addEventListener('click', () => setPaused(false));
  pauseBtn.addEventListener('click', () => setPaused(true));

  const setStateLabel = (s: WSState): void => {
    if (emptyEl === null) return;
    if (s === 'connecting') emptyEl.textContent = 'connecting to spectrum…';
    else if (s === 'closed') emptyEl.textContent = 'reconnecting…';
    else if (s === 'open' && state.totalSeen === 0) emptyEl.textContent = 'connected. waiting for traffic…';
  };

  const ws = new FlockWS({
    url: `${flockServerWsUrl()}/v1/firehose`,
    onBroadcast,
    onState: setStateLabel,
  });
  ws.connect();

  return () => ws.disconnect();
}
